import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import PaymentCountdown from '../../components/PaymentCountdown';

const VerifyPayments = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [verificationRemarks, setVerificationRemarks] = useState('');
  const [editableAmountPaid, setEditableAmountPaid] = useState('');
  const [previousDue, setPreviousDue] = useState('');
  const [amountDue, setAmountDue] = useState('0.00');
  const [processing, setProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [historyPayments, setHistoryPayments] = useState([]);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [modalScreenshotUrl, setModalScreenshotUrl] = useState(null);
  const [loadingScreenshot, setLoadingScreenshot] = useState(false);

  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPaymentMode, setFilterPaymentMode] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchPendingPayments();
    // Check if USN is in URL params
    const params = new URLSearchParams(location.search);
    const usn = params.get('usn');
    if (usn) {
      setSearchQuery(usn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (selectedPayment) {
      loadModalScreenshot(selectedPayment.id);
      // Initialize editable fields with current values
      setEditableAmountPaid(selectedPayment.amountPaid || '');
      setPreviousDue(selectedPayment.previousDue || '');
      
      // Calculate initial amountDue if previousDue exists
      if (selectedPayment.previousDue) {
        const due = parseFloat(selectedPayment.previousDue) - parseFloat(selectedPayment.amountPaid);
        setAmountDue(due.toFixed(2));
      } else {
        setAmountDue('0.00');
      }
    } else {
      // Cleanup when modal closes
      if (modalScreenshotUrl) {
        // Only revoke if it's a blob URL (starts with blob:)
        if (modalScreenshotUrl.startsWith('blob:')) {
          window.URL.revokeObjectURL(modalScreenshotUrl);
        }
        setModalScreenshotUrl(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayment]);

  // Auto-calculate amount due when previousDue or amountPaid changes
  useEffect(() => {
    if (previousDue && editableAmountPaid) {
      const due = parseFloat(previousDue) - parseFloat(editableAmountPaid);
      setAmountDue(due.toFixed(2));
    } else if (previousDue) {
      const due = parseFloat(previousDue) - (selectedPayment ? parseFloat(selectedPayment.amountPaid) : 0);
      setAmountDue(due.toFixed(2));
    } else {
      setAmountDue('0.00');
    }
  }, [previousDue, editableAmountPaid, selectedPayment]);

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/fees/pending-verifications');
      setPayments(response.data.data.transactions);
    } catch (error) {
      console.error('Failed to fetch pending payments:', error);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (paymentId, action) => {
    try {
      setProcessing(true);
      const payload = {
        action,
        verificationRemarks
      };
      
      // Include editable fields only if modified
      if (action === 'VERIFIED') {
        if (editableAmountPaid && parseFloat(editableAmountPaid) !== parseFloat(selectedPayment.amountPaid)) {
          payload.amountPaid = parseFloat(editableAmountPaid);
        }
        if (previousDue) {
          payload.previousDue = parseFloat(previousDue);
        }
      }
      
      const response = await api.put(`/fees/verify/${paymentId}`, payload);
      
      // Show the appropriate message from backend
      toast.success(response.data.message);
      
      setSelectedPayment(null);
      setVerificationRemarks('');
      setEditableAmountPaid('');
      setPreviousDue('');
      setAmountDue('0.00');
      fetchPendingPayments();
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error(error.response?.data?.error || 'Failed to verify payment');
    } finally {
      setProcessing(false);
    }
  };

  const loadModalScreenshot = async (paymentId) => {
    try {
      setLoadingScreenshot(true);
      const response = await api.get(`/fees/screenshot/${paymentId}`);
      
      // Check if response contains presigned URL
      if (response.data.data && response.data.data.viewUrl) {
        // Use presigned URL directly
        setModalScreenshotUrl(response.data.data.viewUrl);
      } else {
        // Fallback to blob (for backward compatibility)
        const blobResponse = await api.get(`/fees/screenshot/${paymentId}`, {
          responseType: 'blob'
        });
        const blob = new Blob([blobResponse.data]);
        const url = window.URL.createObjectURL(blob);
        setModalScreenshotUrl(url);
      }
    } catch (error) {
      console.error('Load screenshot error:', error);
      toast.error('Failed to load screenshot');
    } finally {
      setLoadingScreenshot(false);
    }
  };

  const closeScreenshot = () => {
    if (screenshotUrl) {
      window.URL.revokeObjectURL(screenshotUrl);
      setScreenshotUrl(null);
    }
    setShowScreenshot(false);
  };

  const getFeeCategoryLabel = (category, description) => {
    const categories = {
      APPLICATION_PROSPECTUS: 'Application Fee',
      TUITION: 'Tuition Fee',
      COLLEGE: 'College Fee',
      TRANSPORT: 'Transport Fee',
      HOSTEL: 'Hostel Fee',
      VTU_REGN: 'VTU Registration',
      VTU_EXAM: 'VTU Exam Fee',
      VTU_REVAL: 'VTU Reval/Backlog',
      OTHER: description || 'Other Fee'
    };
    return categories[category] || category;
  };

  // Filter and search logic
  const filteredPayments = payments.filter(payment => {
    const matchesSearch = searchQuery === '' || 
      payment.user?.usn?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.user?.collegeEmail?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.transactionId?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = filterStatus === '' || payment.verificationStatus === filterStatus;
    const matchesPaymentMode = filterPaymentMode === '' || payment.paymentMode === filterPaymentMode;
    
    return matchesSearch && matchesStatus && matchesPaymentMode;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchQuery, filterStatus, filterPaymentMode]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const totalAmount = payments.reduce((sum, p) => sum + parseFloat(p.amountPaid), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">Payment Verification</h1>
              <p className="text-gray-600 text-lg">Review and verify student payment submissions</p>
            </div>
            <button
              onClick={() => navigate('/fees/logs')}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-all shadow-sm hover:shadow-md font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View History
            </button>
          </div>

          {/* Statistics Cards */}
          {payments.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Pending Verifications</p>
                    <p className="text-3xl font-bold text-gray-900">{payments.length}</p>
                  </div>
                  <div className="bg-orange-100 rounded-full p-3">
                    <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Total Amount</p>
                    <p className="text-3xl font-bold text-gray-900">₹{totalAmount.toLocaleString()}</p>
                  </div>
                  <div className="bg-blue-100 rounded-full p-3">
                    <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">Action Required</p>
                    <p className="text-3xl font-bold text-gray-900">{filteredPayments.length}</p>
                  </div>
                  <div className="bg-green-100 rounded-full p-3">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <input
                type="text"
                placeholder="Search by name or USN..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>

            {/* Payment Mode Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
              <select
                value={filterPaymentMode}
                onChange={(e) => setFilterPaymentMode(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Modes</option>
                <option value="ONLINE">Online</option>
                <option value="CASH">Cash</option>
                <option value="CHEQUE">Cheque</option>
                <option value="DD">DD</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing {currentPayments.length} of {filteredPayments.length} payments
            {searchQuery && ` matching "${searchQuery}"`}
          </div>
        </div>

        {filteredPayments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-16 text-center">
            <div className="bg-green-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600 text-lg">No pending payment verifications at the moment</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Student Details
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Amount
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Payment Info
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Date
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentPayments.map((payment, index) => (
                    <tr key={payment.id} className="hover:bg-blue-50 transition-colors duration-150">
                      <td className="px-6 py-5">
                        <div className="flex items-start gap-3">
                          <div className="bg-blue-100 rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0">
                            <span className="text-blue-700 font-bold text-sm">{index + 1}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 text-base">{payment.user.name}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">{payment.user.usn}</span>
                            </div>
                            <div className="text-xs text-gray-600 mt-1">
                              {payment.user.department} • Semester {payment.user.semester?.replace('SEM_', '')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-2xl font-bold text-gray-900">₹{parseFloat(payment.amountPaid).toLocaleString()}</span>
                          <span className="text-xs text-gray-500 mt-1">Indian Rupees</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="space-y-2">
                          <span className={
                            `inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full ${
                              payment.paymentMode === 'UPI' ? 'bg-purple-100 text-purple-800' :
                              payment.paymentMode === 'CASH' ? 'bg-green-100 text-green-800' :
                              payment.paymentMode === 'CARD' ? 'bg-blue-100 text-blue-800' :
                              payment.paymentMode === 'NEFT' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-gray-100 text-gray-800'
                            }`
                          }>
                            {payment.paymentMode === 'UPI' && (
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z"/>
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/>
                              </svg>
                            )}
                            {payment.paymentMode}
                          </span>
                          <div className="text-xs font-medium text-gray-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                            {getFeeCategoryLabel(payment.feeCategory, payment.feeDescription)}
                          </div>
                          <div className="mt-1">
                            <PaymentCountdown 
                              expiresAt={payment.expiresAt} 
                              status={payment.status}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(payment.createdAt).toLocaleDateString('en-IN', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {new Date(payment.createdAt).toLocaleTimeString('en-IN', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => setSelectedPayment(payment)}
                            className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            Review Payment
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  {/* Desktop Pagination */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNum = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-4 py-2 rounded-lg ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="px-2">...</span>;
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>

                  {/* Mobile Pagination */}
                  <div className="flex sm:hidden items-center gap-2 w-full">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ←
                    </button>
                    <select
                      value={currentPage}
                      onChange={(e) => handlePageChange(Number(e.target.value))}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      {[...Array(totalPages)].map((_, index) => (
                        <option key={index + 1} value={index + 1}>
                          Page {index + 1} of {totalPages}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      →
                    </button>
                  </div>

                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages} • {filteredPayments.length} total payments
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Verification Modal */}
        {selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-6 rounded-t-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">Payment Verification</h3>
                    <p className="text-blue-100 text-sm mt-1">Review payment details and screenshot</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedPayment(null);
                      setVerificationRemarks('');
                    }}
                    className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Payment Details */}
                  <div className="space-y-6">
                    {/* Student Info Card */}
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Student Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{selectedPayment.user.name}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-3 py-1 bg-white border border-blue-300 text-blue-900 rounded-lg text-sm font-semibold">
                              {selectedPayment.user.usn}
                            </span>
                            <span className="px-3 py-1 bg-white border border-blue-300 text-blue-900 rounded-lg text-sm">
                              {selectedPayment.user.department}
                            </span>
                            <span className="px-3 py-1 bg-white border border-blue-300 text-blue-900 rounded-lg text-sm">
                              Sem {selectedPayment.user.semester?.replace('SEM_', '')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Payment Details Card */}
                    <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Payment Details (Editable)
                      </h4>
                      <div className="grid grid-cols-2 gap-6">
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-gray-500 uppercase block mb-2">
                            Amt Fees/Previous Due (₹)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={previousDue}
                            onChange={(e) => setPreviousDue(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold text-gray-900"
                            placeholder="Enter previous balance or total fees due"
                          />
                          <p className="text-xs text-gray-500 mt-1">Enter the total amount due before this payment</p>
                        </div>
                        
                        <div className="col-span-2">
                          <label className="text-xs font-medium text-gray-500 uppercase block mb-2">
                            Amount Paid (₹)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editableAmountPaid}
                            onChange={(e) => setEditableAmountPaid(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-2xl font-bold text-green-600"
                            placeholder="Amount paid by student"
                          />
                          <p className="text-xs text-gray-500 mt-1">Modify if the amount needs correction</p>
                        </div>
                        
                        <div className="col-span-2 bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-lg border-2 border-orange-300">
                          <label className="text-xs font-medium text-orange-700 uppercase block mb-2">
                            Amount Due (Auto-calculated)
                          </label>
                          <p className="text-3xl font-bold text-orange-600">
                            ₹{parseFloat(amountDue).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-orange-600 mt-2">
                            = Previous Due - Amount Paid
                          </p>
                        </div>
                        
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase block mb-2">Payment Mode</label>
                          <span className={
                            `inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg ${
                              selectedPayment.paymentMode === 'UPI' ? 'bg-purple-100 text-purple-800' :
                              selectedPayment.paymentMode === 'CASH' ? 'bg-green-100 text-green-800' :
                              selectedPayment.paymentMode === 'CARD' ? 'bg-blue-100 text-blue-800' :
                              selectedPayment.paymentMode === 'NEFT' ? 'bg-indigo-100 text-indigo-800' :
                              'bg-gray-100 text-gray-800'
                            }`
                          }>
                            {selectedPayment.paymentMode}
                          </span>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase block mb-2">Category</label>
                          <span className="inline-flex items-center px-3 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold">
                            {getFeeCategoryLabel(selectedPayment.feeCategory, selectedPayment.feeDescription)}
                          </span>
                        </div>
                        {selectedPayment.upiTransactionId && (
                          <div className="col-span-2">
                            <label className="text-xs font-medium text-gray-500 uppercase block mb-2">Transaction ID</label>
                            <p className="text-base font-mono bg-gray-100 text-gray-900 px-4 py-3 rounded-lg border border-gray-300">
                              {selectedPayment.upiTransactionId}
                            </p>
                          </div>
                        )}
                        {selectedPayment.remarks && (
                          <div className="col-span-2">
                            <label className="text-xs font-medium text-gray-500 uppercase block mb-2">Student Remarks</label>
                            <p className="text-sm text-gray-700 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                              {selectedPayment.remarks}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Verification Remarks */}
                    <div className="bg-white rounded-xl p-6 border-2 border-gray-200">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Verification Remarks
                      </label>
                      <textarea
                        value={verificationRemarks}
                        onChange={(e) => setVerificationRemarks(e.target.value)}
                        rows="4"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        placeholder="Add your verification remarks here... (optional)"
                      />
                    </div>
                  </div>

                  {/* Right Column - Payment Screenshot */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-300">
                      <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Payment Screenshot
                      </h4>
                      
                      {loadingScreenshot ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg">
                          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
                          <p className="text-gray-600 font-medium">Loading screenshot...</p>
                        </div>
                      ) : modalScreenshotUrl ? (
                        <div className="bg-white rounded-lg overflow-hidden shadow-lg border-2 border-gray-200">
                          <img
                            src={modalScreenshotUrl}
                            alt="Payment Screenshot"
                            className="w-full h-auto object-contain max-h-[600px]"
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border-2 border-dashed border-gray-300">
                          <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-gray-500 font-medium">No screenshot available</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 mt-8 pt-6 border-t-2 border-gray-200">
                  <button
                    onClick={() => {
                      setSelectedPayment(null);
                      setVerificationRemarks('');
                      setEditableAmountPaid('');
                      setPreviousDue('');
                      setAmountDue('0.00');
                    }}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-base transition-all"
                    disabled={processing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleVerify(selectedPayment.id, 'REJECTED')}
                    className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    disabled={processing}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    {processing ? 'Rejecting...' : 'Reject Payment'}
                  </button>
                  <button
                    onClick={() => handleVerify(selectedPayment.id, 'VERIFIED')}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold text-base shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    disabled={processing}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {processing ? 'Approving...' : 'Approve Payment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Screenshot Modal */}
        {showScreenshot && screenshotUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={closeScreenshot}>
            <div className="relative max-w-6xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={closeScreenshot}
                className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-10"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={screenshotUrl}
                alt="Payment Screenshot"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Verification History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6">
                {historyPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No verification history found</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {historyPayments.map((payment) => (
                      <div key={payment.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <label className="text-xs font-medium text-gray-500">Student</label>
                            <p className="font-medium text-gray-900">{payment.user.name}</p>
                            <p className="text-sm text-gray-500">{payment.user.usn}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">Amount</label>
                            <p className="text-lg font-bold text-gray-900">₹{payment.amountPaid}</p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">Status</label>
                            <p>
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                payment.verificationStatus === 'VERIFIED'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {payment.verificationStatus}
                              </span>
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500">Verified By</label>
                            <p className="text-sm text-gray-900">{payment.verifiedBy?.name || 'N/A'}</p>
                            <p className="text-xs text-gray-500">
                              {payment.verifiedAt ? new Date(payment.verifiedAt).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                        {payment.verificationRemarks && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <label className="text-xs font-medium text-gray-500">Remarks</label>
                            <p className="text-sm text-gray-700 mt-1">{payment.verificationRemarks}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyPayments;
